// Feedback — single-purpose page that lets any signed-in user send a free-
// text message to the team inbox (tyler@rolloverrep.com). The send happens
// via the `sendFeedback` Cloud Function (see functions/index.js) so SMTP
// credentials never touch the client.
//
// Available to both operators and customer-CRM users — feedback is one of
// the few PM-adjacent surfaces non-admins can reach (the rest of /Projects
// is operator-only per the security requirement).

import { useState } from 'react';
import { IonContent, IonPage } from '@ionic/react';

import { sendFeedback } from '../firebase';

// Same hard cap the server enforces. Mirrored client-side so the user gets
// instant feedback (countdown + disabled submit) instead of a round-trip
// rejection.
const MAX_LENGTH = 5000;

type Status =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'success' }
  | { kind: 'error'; message: string };

const Feedback: React.FC = () => {
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<Status>({ kind: 'idle' });

  const trimmed = message.trim();
  const canSubmit = trimmed.length > 0 && trimmed.length <= MAX_LENGTH && status.kind !== 'sending';

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setStatus({ kind: 'sending' });
    try {
      // Pass the current URL as context so the email shows where the user
      // was when they clicked Submit — saves a round-trip if the feedback
      // is about a specific page.
      await sendFeedback(trimmed, window.location.href);
      setStatus({ kind: 'success' });
      setMessage('');
    } catch (err: any) {
      // Callable errors come through as { code, message } — surface the
      // user-readable message if there is one, otherwise a generic fallback.
      const msg =
        (err && typeof err.message === 'string' && err.message) ||
        'Something went wrong sending your feedback. Please try again.';
      setStatus({ kind: 'error', message: msg });
    }
  }

  return (
    <IonPage>
      <IonContent fullscreen>
        <div className="pm-app">
          <div className="pm-page" style={{ maxWidth: 720, padding: '24px 28px' }}>
            <div className="pm-header">
              <div className="pm-header-titles">
                <h1>Send feedback</h1>
                <p>
                  Hit a bug? Want a feature? Anything you write here goes
                  straight to the Rollover Rep team.
                </p>
              </div>
            </div>

            {/* NOTE: do NOT wrap in .pm-stat-cards — that's a 4-column grid
                for stat tiles. A form needs to span full width, so we use
                a plain card built from the design tokens directly. */}
            <form
              onSubmit={handleSubmit}
              style={{
                marginTop: 16,
                background: 'var(--rr-paper, #fff)',
                border: '1px solid var(--pm-border, var(--rr-stone-300))',
                borderRadius: 'var(--pm-radius-lg, 12px)',
                padding: '16px 18px',
              }}
            >
              <div>
                <label
                  htmlFor="feedback-message"
                  className="pm-stat-label"
                  style={{ display: 'block', marginBottom: 8 }}
                >
                  Your feedback
                </label>
                <textarea
                  id="feedback-message"
                  value={message}
                  onChange={(e) => {
                    setMessage(e.target.value);
                    // Reset success/error banners as soon as the user starts
                    // typing a new message so stale state doesn't linger.
                    if (status.kind === 'success' || status.kind === 'error') {
                      setStatus({ kind: 'idle' });
                    }
                  }}
                  rows={8}
                  placeholder="Tell us what's working, what's broken, or what would make your day easier…"
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: 12,
                    borderRadius: 8,
                    border: '1px solid var(--pm-border, var(--rr-stone-300))',
                    background: 'var(--rr-paper, #fff)',
                    color: 'var(--rr-ink, #111)',
                    font: 'inherit',
                    resize: 'vertical',
                  }}
                />
                <div
                  style={{
                    marginTop: 6,
                    fontSize: 12,
                    color:
                      trimmed.length > MAX_LENGTH
                        ? 'var(--rr-danger, #b00020)'
                        : 'var(--rr-muted, #777)',
                    textAlign: 'right',
                  }}
                >
                  {trimmed.length} / {MAX_LENGTH}
                </div>

                {status.kind === 'success' ? (
                  <div
                    role="status"
                    style={{
                      marginTop: 12,
                      padding: '10px 12px',
                      borderRadius: 8,
                      background: 'rgba(20, 130, 80, 0.08)',
                      color: 'var(--rr-success, #14823c)',
                      fontSize: 14,
                    }}
                  >
                    Thanks! Your feedback is on its way.
                  </div>
                ) : null}
                {status.kind === 'error' ? (
                  <div
                    role="alert"
                    style={{
                      marginTop: 12,
                      padding: '10px 12px',
                      borderRadius: 8,
                      background: 'rgba(176, 0, 32, 0.08)',
                      color: 'var(--rr-danger, #b00020)',
                      fontSize: 14,
                    }}
                  >
                    {status.message}
                  </div>
                ) : null}

                <div style={{ marginTop: 12, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button
                    type="submit"
                    className="pm-button pm-button-primary"
                    disabled={!canSubmit}
                  >
                    {status.kind === 'sending' ? 'Sending…' : 'Send feedback'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Feedback;
